import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth as SupabaseAuth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from '../lib/supabase';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button/button';
import { Input } from './ui/input';
import { ArrowLeft } from 'lucide-react';
import { AuthSignUp } from './AuthSignUp';

export function Auth() {
  const isResetting = window.location.pathname === '/auth/reset';
  const unconfirmedParam = new URLSearchParams(window.location.search).has(
    'unconfirmed',
  );
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(
    new URLSearchParams(window.location.search).has('signup'),
  );
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(
    unconfirmedParam ? 'Please confirm your email before signing in.' : null,
  );
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handlePasswordReset = async () => {
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('You must be logged in to reset your password.');
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) throw updateError;

      setSuccess(true);

      setTimeout(() => {
        navigate('/agent-builder');
      }, 2000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update password',
      );
    }
  };

  const handleForgotPassword = async () => {
    setError(null);

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/callback`,
      });

      if (error) throw error;

      setSuccess(true);
      setError('Check your email for the password reset link');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to send reset email',
      );
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-prompt-safecomgallery p-4">
      <Card
        className={`w-full bg-prompt-safecomfantasy ${isSigningUp ? 'max-w-lg' : 'max-w-md'}`}
      >
        <CardHeader className="space-y-4">
          {isSigningUp && (
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={() => setIsSigningUp(false)}
                className="text-prompt-safecomabbey hover:text-prompt-safecomvulcan"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            </div>
          )}
          <div className="flex justify-center">
            <img
              src="/marque-full-colour-svg.svg"
              alt="PromptSafe Logo"
              className="h-20 w-auto"
            />
          </div>
          <CardTitle className="text-center text-2xl font-bold">
            {isResetting
              ? 'Reset your password'
              : isForgotPassword
                ? 'Forgot password'
                : isSigningUp
                  ? 'Create your account'
                  : 'Welcome to PromptSafe'}
          </CardTitle>
          {!isResetting && !isForgotPassword && (
            <CardDescription className="text-balance text-center">
              See how your AI agent handles hard conversations, before your
              users do.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {isSigningUp ? (
            <AuthSignUp onBackToSignIn={() => setIsSigningUp(false)} />
          ) : isResetting ? (
            success ? (
              <div className="text-center text-green-600">
                Password updated successfully! Redirecting...
              </div>
            ) : (
              <>
                <Input
                  type="password"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full bg-white"
                />
                <Input
                  type="password"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-white"
                />
                {error && (
                  <div className="text-center text-sm text-red-600">
                    {error}
                  </div>
                )}
                <Button
                  onClick={handlePasswordReset}
                  className="hover:bg-prompt-safecomoutrageous-orange/90 w-full bg-prompt-safecomoutrageous-orange text-white"
                >
                  Update password
                </Button>
              </>
            )
          ) : isForgotPassword ? (
            <>
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white"
              />
              {error && (
                <div
                  className={`text-center text-sm ${error.includes('Check your email') ? 'text-green-600' : 'text-red-600'}`}
                >
                  {error}
                </div>
              )}
              <Button
                onClick={handleForgotPassword}
                className="hover:bg-prompt-safecomoutrageous-orange/90 w-full bg-prompt-safecomoutrageous-orange text-white"
              >
                Send reset link
              </Button>
              <Button
                variant="ghost"
                onClick={() => setIsForgotPassword(false)}
                className="w-full"
              >
                Back to Login
              </Button>
            </>
          ) : (
            <>
              {error && (
                <div className="text-center text-sm text-red-600">{error}</div>
              )}
              <SupabaseAuth
                supabaseClient={supabase}
                appearance={{
                  theme: ThemeSupa,
                  variables: {
                    default: {
                      colors: {
                        brand: '#ff5a35',
                        brandAccent: '#ff5a35',
                        inputBackground: 'white',
                      },
                    },
                  },
                }}
                providers={[]}
                redirectTo={`${window.location.origin}/auth/callback`}
                showLinks={false}
                theme="light"
                view="sign_in"
              />
              <Button
                variant="ghost"
                onClick={() => setIsForgotPassword(true)}
                className="hover:text-prompt-safecomoutrageous-orange/90 w-full text-prompt-safecomoutrageous-orange"
              >
                Forgot password?
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setIsSigningUp(true);
                  setError(null);
                }}
                className="w-full"
              >
                Don't have an account? Sign up
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
